import React from "react";
import { Link } from "react-router-dom";
import ProfitLineChart from "../components/ProfitLineChart";
import ProfitStatCard from "../components/ProfitStatCard";
import { ProfitDownloadIcon, ProfitInfoIcon } from "../components/ProfitIcons";
import {
  formatProfitCurrency,
  monthlyRevenue,
  profitCurrentPeriod,
  profitDashboardKpis,
  profitMerchants,
  profitPayoutSummary,
  profitPeriodOptions,
  profitPortalMeta,
  profitRevenueModelText,
  renderProfitMeta,
  spreadSections,
} from "../data/profitData";

export default function ProfitDashboardPage() {
  const topMerchants = profitMerchants
    .slice()
    .sort(function sortByShare(a, b) {
      return b.share - a.share;
    })
    .slice(0, 7);

  return (
    <div className="profit-page">
      <div className="profit-page-header profit-page-header-row">
        <div>
          <div className="profit-page-title">Partner Overview</div>
          <div className="profit-page-subtitle">
            {profitCurrentPeriod} · {profitPortalMeta.companyName} · {profitPortalMeta.partnerTier} Partner
          </div>
        </div>
        <div className="profit-page-actions">
          <select className="profit-select">
            {profitPeriodOptions.map(function renderOption(period) {
              return <option key={period}>{period}</option>;
            })}
          </select>
          <button className="profit-button profit-button-outline" type="button">
            <ProfitDownloadIcon />
            Export
          </button>
        </div>
      </div>

      <div className="profit-kpi-grid">
        {profitDashboardKpis.map(function renderCard(kpi) {
          return (
            <ProfitStatCard
              key={kpi.label}
              stripeClassName={kpi.stripeClassName}
              label={kpi.label}
              value={kpi.value}
            >
              {renderProfitMeta(kpi)}
            </ProfitStatCard>
          );
        })}
      </div>

      <div className="profit-chart-grid">
        <div className="profit-card">
          <div className="profit-card-header">
            <div>
              <div className="profit-card-title">Revenue Trend</div>
              <div className="profit-card-subtitle">
                Monthly partner share · {profitCurrentPeriod}
              </div>
            </div>
            <div className="profit-segmented">
              <button className="profit-button profit-button-primary" type="button">
                Monthly
              </button>
              <button className="profit-button profit-button-outline" type="button">
                Weekly
              </button>
            </div>
          </div>
          <ProfitLineChart data={monthlyRevenue} />
        </div>

        <div className="profit-card profit-scroll-card">
          <div className="profit-card-header">
            <div>
              <div className="profit-card-title">MDR Spread</div>
              <div className="profit-card-subtitle">By merchant tier</div>
            </div>
          </div>
          {spreadSections.map(function renderSection(section) {
            return (
              <div className="profit-pay-section" key={section.title}>
                <div className="profit-pay-section-label">{section.title}</div>
                {section.items.map(function renderItem(item) {
                  return (
                    <div className="profit-pay-item" key={item.name}>
                      <div className="profit-pay-item-row">
                        <div className="profit-pay-scheme">
                          <span className="profit-scheme-dot" style={{ backgroundColor: section.color }} />
                          <span className="profit-pay-name">{item.name}</span>
                        </div>
                        <span className="profit-pay-percentage">{item.spread}</span>
                      </div>
                      <div className="profit-pay-bar">
                        <div className="profit-pay-fill" style={{ width: item.width, backgroundColor: section.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          <div className="profit-summary-row">
            <span>Avg MDR spread</span>
            <span className="profit-mono profit-summary-value">1.07%</span>
          </div>
        </div>
      </div>

      <div className="profit-bottom-grid">
        <div className="profit-card">
          <div className="profit-card-header">
            <div className="profit-card-title">Top Merchants by Earnings</div>
            <Link className="profit-button profit-button-ghost" to="/profit/merchants">
              View all →
            </Link>
          </div>
          <div className="profit-merchant-list">
            {topMerchants.map(function renderMerchant(merchant, index) {
              return (
                <div className="profit-merchant-item" key={merchant.id}>
                  <div className={"profit-merchant-rank" + (index === 0 ? " top-1" : index < 3 ? " top-3" : "")}>
                    {index + 1}
                  </div>
                  <div className="profit-merchant-info">
                    <div className="profit-merchant-name">{merchant.name}</div>
                    <div className="profit-merchant-sub">
                      {merchant.txns.toLocaleString()} txns · Spread {merchant.spread.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="profit-merchant-value">
                      {formatProfitCurrency(merchant.share)}
                    </div>
                    <div className="profit-merchant-sub">
                      from SGD {merchant.volume.toFixed(0)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="profit-card">
          <div className="profit-card-header">
            <div>
              <div className="profit-card-title">Payout Status</div>
              <div className="profit-card-subtitle">Current quarter</div>
            </div>
            <Link className="profit-button profit-button-ghost" to="/profit/payouts">
              Details →
            </Link>
          </div>
          <div className="profit-payout-summary">
            <div className="profit-summary-item">
              <span>{profitCurrentPeriod} Accumulated</span>
              <span className="profit-mono profit-summary-value">{profitPayoutSummary.accumulated}</span>
            </div>
            <div className="profit-summary-item">
              <span>Minimum threshold</span>
              <span className="profit-mono">{profitPayoutSummary.threshold}</span>
            </div>
            <div className="profit-progress">
              <div className="profit-progress-fill" />
            </div>
            <div className="profit-progress-meta">
              {profitPayoutSummary.progressText}
            </div>
          </div>
          <div className="profit-detail-list">
            <div className="profit-summary-item">
              <span>Next payout date</span>
              <span>{profitPayoutSummary.nextDate}</span>
            </div>
            <div className="profit-summary-item">
              <span>Payment cycle</span>
              <span>{profitPayoutSummary.paymentCycle}</span>
            </div>
            <div className="profit-summary-item">
              <span>Revenue share</span>
              <span className="profit-summary-highlight">{profitPayoutSummary.revenueShare}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="profit-alert profit-alert-info">
        <ProfitInfoIcon />
        <div>
          <strong>Revenue Share Model:</strong> {profitRevenueModelText}
        </div>
      </div>
    </div>
  );
}
